"use client";

import { useEffect, useState, useMemo } from "react";
import { Save, Settings, Edit3, Info } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Modal from "../layout/Modal";
import MessageManager from "../layout/Message";
import "./EmployeeEditPage.css";
import BackButton from "../shared/backButton";
import { getDepartmentsListApi } from "../../services/settingsService";
import CustomTooltip from "../shared/CustomTooltip";
import {
  getPeopleById,
  editPeopleProfileApi,
  getAllPeopleApi,
  getSuggestedGoalsInsidePeople,
} from "../../services/peopleService";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import { useTranslation } from "react-i18next";
import { use } from "react";

const EmployeeEditPage = ({ onSave }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });

  // const [formData, setFormData] = useState({
  //   name: employee?.name || "",
  //   email: employee?.email || "",
  //   phone: employee?.phone || "",
  //   role: employee?.role || "",
  //   department: employee?.department || "",
  //   level: employee?.level || "Mid-level",
  //   location: employee?.location || "",
  //   manager: employee?.manager || "",
  //   salary: employee?.salary || "",
  //   workStyle: employee?.workStyle || "Hybrid",
  //   joinDate: employee?.joinDate || new Date().toISOString().split("T")[0],
  //   skills: employee?.skills || [],
  //   education: employee?.education || "",
  //   experience: employee?.experience || "",
  //   languages: employee?.languages || [],
  //   interests: employee?.interests || [],
  //   certifications: employee?.certifications || [],
  //   projects: employee?.projects || [],
  //   performanceRating: employee?.performanceRating || "Good",
  //   availability: employee?.availability || "Available",
  //   role: employee?.designation || "",
  // });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    level: "Mid-level",
    location: "",
    manager: "",
    salary: "",
    workStyle: "Hybrid",
    joinDate: new Date().toISOString().split("T")[0],
    skills: [],
    education: "",
    experience: "",
    languages: [],
    interests: [],
    certifications: [],
    projects: [],
    performanceRating: "Good",
    availability: "Available",
    department: "",

    // NEW:
    roleJobId: "", // this is what <select value=...> will use
    roleName: "", // human readable label (designation)
  });

  const [newSkill, setNewSkill] = useState("");
  const [newLanguage, setNewLanguage] = useState("");
  const [newInterest, setNewInterest] = useState("");
  const [newCertification, setNewCertification] = useState("");
  const [newProject, setNewProject] = useState("");

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  // Lookup data
  const departments = [
    t("common.departments.engineering"),
    t("common.departments.product"),
    t("common.departments.analytics"),
    t("common.departments.sales"),
    t("common.departments.marketing"),
    t("common.departments.design"),
    t("common.departments.humanResources"),
  ];
  const levels = [
    t("common.levels.junior"),
    t("common.levels.midLevel"),
    t("common.levels.senior"),
    t("common.levels.lead"),
    t("common.levels.manager"),
    t("common.levels.director"),
  ];
  const workStyles = [
    t("teamCreatePage.workStyles.remote"),
    t("teamCreatePage.workStyles.hybrid"),
    t("teamCreatePage.workStyles.onsite"),
    t("teamCreatePage.workStyles.flexible"),
  ];
  const performanceRatings = [
    t("teamsPage.performanceRatings.excellent"),
    t("teamsPage.performanceRatings.good"),
    t("teamsPage.performanceRatings.average"),
    t("teamsPage.performanceRatings.needsImprovement"),
  ];
  const availabilityOptions = [
    t("teamDetailPage.availability.available"),
    t("teamDetailPage.availability.partiallyAvailable"),
    t("teamDetailPage.availability.notAvailable"),
  ];

  // Sample roles for suggestions
  const commonRoles = [
    t("common.roles.softwareEngineer"),
    t("common.roles.seniorSoftwareEngineer"),
    t("common.roles.productManager"),
    t("common.roles.dataScientist"),
    t("common.roles.uxDesigner"),
    t("common.roles.marketingSpecialist"),
    t("common.roles.salesAssociate"),
    t("common.roles.hrCoordinator"),
    t("common.roles.devOpsEngineer"),
    t("common.roles.businessAnalyst"),
    t("common.roles.projectManager"),
    t("common.roles.qaEngineer"),
  ];

  // Sample locations for suggestions
  const commonLocations = [
    "San Francisco, CA",
    "New York, NY",
    "Austin, TX",
    "Seattle, WA",
    "Los Angeles, CA",
    "Chicago, IL",
    "Denver, CO",
    "Miami, FL",
    "Boston, MA",
    "Portland, OR",
    "Atlanta, GA",
    "Dallas, TX",
  ];

  const handleAddSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, newSkill.trim()],
      });
      setNewSkill("");
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter((skill) => skill !== skillToRemove),
    });
  };

  const handleAddLanguage = () => {
    if (
      newLanguage.trim() &&
      !formData.languages.includes(newLanguage.trim())
    ) {
      setFormData({
        ...formData,
        languages: [...formData.languages, newLanguage.trim()],
      });
      setNewLanguage("");
    }
  };

  const handleRemoveLanguage = (languageToRemove) => {
    setFormData({
      ...formData,
      languages: formData.languages.filter(
        (language) => language !== languageToRemove,
      ),
    });
  };

  const handleAddInterest = () => {
    if (
      newInterest.trim() &&
      !formData.interests.includes(newInterest.trim())
    ) {
      setFormData({
        ...formData,
        interests: [...formData.interests, newInterest.trim()],
      });
      setNewInterest("");
    }
  };

  const handleRemoveInterest = (interestToRemove) => {
    setFormData({
      ...formData,
      interests: formData.interests.filter(
        (interest) => interest !== interestToRemove,
      ),
    });
  };

  const handleAddCertification = () => {
    if (
      newCertification.trim() &&
      !formData.certifications.includes(newCertification.trim())
    ) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, newCertification.trim()],
      });
      setNewCertification("");
    }
  };

  const handleRemoveCertification = (certificationToRemove) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter(
        (certification) => certification !== certificationToRemove,
      ),
    });
  };

  const handleAddProject = () => {
    if (newProject.trim() && !formData.projects.includes(newProject.trim())) {
      setFormData({
        ...formData,
        projects: [...formData.projects, newProject.trim()],
      });
      setNewProject("");
    }
  };

  const handleRemoveProject = (projectToRemove) => {
    setFormData({
      ...formData,
      projects: formData.projects.filter(
        (project) => project !== projectToRemove,
      ),
    });
  };

  const navigate = useNavigate();
  const onNavigate = () => {
    // console.log("Navigating to:", page);
    // navigate(page);
    navigate(`/employees/${paramId}`);
  };
  const paramId = useParams().id;

  useEffect(() => {
    getDepartmentsListApiHandler();

    getManagerDropdownApiHandler();
  }, []);

  const [departmentsList, setDepartmentsList] = useState([]);
  const getDepartmentsListApiHandler = async () => {
    try {
      const response = await getDepartmentsListApi();
      setDepartmentsList(response.data ? [...response.data] : []);
      if (response.code === 200) {
        await getUserByIdApiHandler();
        // console.log("Departments fetched successfully:", response.data);
      }
    } catch (error) {
      console.error("Error fetching departments list:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const [roleSuggestions, setRoleSuggestions] = useState([]);
  // roles dropdown state (two groups only: Suggested + All)
  const [roleOptions, setRoleOptions] = useState({
    suggestedRoles: [],
    allJobRoles: [],
  });
  const [rolesLoading, setRolesLoading] = useState(false);

  // helpers
  const normalize = (list = []) =>
    list
      .filter((r) => r?.jobId && r?.roleName)
      .map((r) => ({
        jobId: String(r.jobId).trim(),
        roleName: String(r.roleName).trim(),
        matchPercentage: Number(r.matchPercentage) || 0,
      }));

  const uniqBy = (arr, keyFn) =>
    Array.from(new Map(arr.map((x) => [keyFn(x), x])).values());

  const suggestedList = useMemo(() => {
    const clean = normalize(roleOptions.suggestedRoles);
    const deduped = uniqBy(clean, (x) => x.jobId);
    return deduped.sort(
      (a, b) =>
        (b.matchPercentage ?? -1) - (a.matchPercentage ?? -1) ||
        a.roleName.localeCompare(b.roleName) ||
        a.jobId.localeCompare(b.jobId),
    );
  }, [roleOptions.suggestedRoles]);

  const allList = useMemo(() => {
    const clean = normalize(roleOptions.allJobRoles);
    const deduped = uniqBy(clean, (x) => x.jobId);
    // optional: exclude items already shown in Suggested
    const suggestedIds = new Set(suggestedList.map((s) => s.jobId));
    return deduped
      .filter((x) => !suggestedIds.has(x.jobId))
      .sort(
        (a, b) =>
          a.roleName.localeCompare(b.roleName) ||
          a.jobId.localeCompare(b.jobId),
      );
  }, [roleOptions.allJobRoles, suggestedList]);

  // given: apiData = { suggestedRoles: [], allJobRoles: [...] }

  const groupRolesByTitle = (list = []) => {
    const map = new Map();
    for (const { roleName = "Unknown", jobId } of list) {
      const key = roleName.trim() || "Unknown";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ roleName: key, jobId });
    }
    // sort groups by role name; inside each group sort by jobId
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([roleName, items]) => ({
        roleName,
        items: items.sort((a, b) => a.jobId.localeCompare(b.jobId)),
      }));
  };

  const groupedRoles = groupRolesByTitle(roleSuggestions);

  const [candidate, setCandidate] = useState({});
  //userId of the employee profile
  // const getUserByIdApiHandler = async () => {
  //   console.log("Fetching user by ID:", paramId);
  //   try {
  //     const response = await getPeopleById(paramId);
  //     setCandidate(response.data);
  //     formData.name = response.data.name;
  //     formData.email = response.data.userId;
  //     formData.level = response.data.level;
  //     formData.manager = response.data.manager;
  //     formData.workStyle = response.data.workStyle;
  //     formData.performanceRating = response.data.performanceRating;
  //     formData.availability = response.data.availability;
  //     formData.role = response.data.designation || "";
  //   } catch (error) {
  //     handleApiError(error, {
  //       showModal: true,
  //       showButton: false,
  //     });
  //   }
  // };

  // const getUserByIdApiHandler = async () => {
  //   try {
  //     const { data } = await getPeopleById(paramId);
  //     setCandidate(data);
  //     if (data.code === 200) {
  //       getRoleSuggestions(paramId);
  //     }
  //     setFormData((prev) => ({
  //       ...prev,
  //       name: data.name ?? prev.name,
  //       email: data.userId ?? prev.email,
  //       level: data.level ?? prev.level,
  //       manager: data.manager ?? prev.manager,
  //       workStyle: data.workStyle ?? prev.workStyle,
  //       performanceRating: data.performanceRating ?? prev.performanceRating,
  //       availability: data.availability ?? prev.availability,
  //       // keep the raw designation until we map it to a jobId
  //       roleName: data.mappedRoleName || prev.mappedRoleName || "",
  //       department: data.department || prev.department || "",
  //     }));
  //     // getRoleSuggestions(paramId);
  //   } catch (error) {
  //     handleApiError(error, { showModal: true, showButton: false });
  //   }
  // };

  // getUserByIdApiHandler
  const getUserByIdApiHandler = async () => {
    try {
      const res = await getPeopleById(paramId);
      const payload = res?.data ?? res;

      setCandidate(payload);
      setFormData((prev) => ({
        ...prev,
        name: payload?.name ?? prev.name,
        email: payload?.userId ?? prev.email,
        level: payload?.level ?? prev.level,
        manager: payload?.manager ?? prev.manager,
        workStyle: payload?.workStyle ?? prev.workStyle,
        performanceRating: payload?.performanceRating ?? prev.performanceRating,
        availability: payload?.availability ?? prev.availability,
        roleName: payload?.mappedRoleName ?? prev?.mappedRoleName ?? "",
        department: payload?.department ?? prev?.department ?? "",
      }));

      const ok =
        (res?.status >= 200 && res?.status < 300) ||
        Number(payload?.code) === 200 ||
        payload?.success === true;

      if (ok) {
        // ✅ pass the fresh department directly
        await getRoleSuggestions(paramId, payload?.department ?? "");
      }
    } catch (error) {
      console.error("getUserByIdApiHandler failed", error);
      handleApiError(error, { showModal: true, showButton: false });
    }
  };

  const getRoleSuggestions = async (userId, deptArg) => {
    try {
      const department = deptArg ?? formData.department ?? "";
      const obj = {
        userId: userId,
        department, // ✅ no longer empty
      };
      setRolesLoading(true);
      const res = await getSuggestedGoalsInsidePeople(obj);

      const payload = res?.data ?? res ?? {};
      const suggestedRoles = Array.isArray(payload.suggestedRoles)
        ? payload.suggestedRoles
        : [];
      const allJobRoles = Array.isArray(payload.allJobRoles)
        ? payload.allJobRoles
        : [];

      setRoleOptions({ suggestedRoles, allJobRoles });
    } catch (error) {
      handleApiError(error, { showModal: false });
      setRoleOptions({ suggestedRoles: [], allJobRoles: [] });
    } finally {
      setRolesLoading(false);
    }
  };
  useEffect(() => {
    if (!paramId) return;
    if (!formData.department) return;
    // debounce if needed
    getRoleSuggestions(paramId, formData.department);
  }, [paramId, formData.department]);

  // const getRoleSuggestions = async (userId, department) => {
  //   try {
  //     let obj = {
  //
  //       department: formData.department || "",
  //     };
  //     setRolesLoading(true);
  //     const res = await getSuggestedGoalsInsidePeople(obj);

  //     // handle either { data: {...} } or direct payload
  //     const payload = res?.data ?? res ?? {};
  //     const suggestedRoles = Array.isArray(payload.suggestedRoles)
  //       ? payload.suggestedRoles
  //       : [];
  //     const allJobRoles = Array.isArray(payload.allJobRoles)
  //       ? payload.allJobRoles
  //       : [];

  //     setRoleOptions({ suggestedRoles, allJobRoles });
  //   } catch (error) {
  //     handleApiError(error, { showModal: false });
  //     setRoleOptions({ suggestedRoles: [], allJobRoles: [] });
  //   } finally {
  //     setRolesLoading(false);
  //   }
  // };

  // helper to normalize text (handles "enginneer" vs "engineer" mismatches better)
  const norm = (s = "") =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9 ]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  useEffect(() => {
    if (rolesLoading) return;

    const desig = candidate?.mappedRoleName || formData.roleName;
    if (!desig) return;
    if (formData.roleJobId) return; // already selected

    const combined = [...suggestedList, ...allList];
    if (!combined.length) return;

    const nd = norm(desig);

    // 1) exact (normalized) match
    let pick =
      combined.find((r) => norm(r.roleName) === nd) ||
      // 2) partial contains either way
      combined.find(
        (r) => norm(r.roleName).includes(nd) || nd.includes(norm(r.roleName)),
      );

    // 3) simple token-overlap fallback (helps with typos like "enginneer")
    if (!pick) {
      const words = nd.split(" ").filter(Boolean);
      pick = combined
        .map((r) => ({
          ...r,
          score: words.filter((w) => norm(r.roleName).includes(w)).length,
        }))
        .sort((a, b) => b.score - a.score)[0];
      if (pick?.score === 0) pick = null;
    }

    if (pick) {
      setFormData((prev) => ({
        ...prev,
        roleJobId: pick.jobId,
        roleName: pick.roleName,
      }));
    }
  }, [rolesLoading, suggestedList, allList, candidate?.mappedRoleName]);

  const handleModalClose = () => {
    setShowModal({ show: false, type: "", title: "", message: "" });
    onNavigate(`/employees/${paramId}`);
  };

  const [managers, setManagers] = useState([]);
  const getManagerDropdownApiHandler = async () => {
    try {
      const response = await getAllPeopleApi({ level: "Manager" });
      console.log("Managers fetched successfully:", response.data);
      setManagers(
        response.data.map((person) => ({
          name: person.name,
          id: person.userId,
        })),
      );
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    }
  };
  const handleSubmitApiHandler = async () => {
    console.log("Submitting employee data:", formData);
    const obj = {
      level: formData.level,
      availability: formData.availability,
      // languages: formData.languages,

      userId: paramId,
      workStyle: formData.workStyle,
      performanceRating: formData.performanceRating,
      manager: formData.manager,
      mappedJobId: formData.roleJobId,
      mappedRoleName: formData.roleName,
      department: formData.department,
    };
    console.log("Data to be submitted:", obj);
    try {
      const response = await editPeopleProfileApi(obj);
      console.log("Employee updated successfully:", response.data);
      MessageManager.showToast(
        "success",
        t("employeeEdit.actions.employeeUpdated"),
      );
      getUserByIdApiHandler();
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    }
  };
  return (
    <div className="employee-edit-page">
      <BackButton />

      <main className="employee-edit-main">
        <div className="container">
          <div className="page-header">
            <div className="header-icon">
              <Edit3 size={32} />
            </div>
            <h1>
              {t("employeeEdit.pageTitle")} {formData?.name}
            </h1>
            <p>{t("employeeEdit.pageDescription")}</p>
          </div>

          <form className="employee-edit-form">
            <div className="form-section">
              <h2>
                <Settings size={20} />
                {t("employeeEdit.basicInformation")}
              </h2>

              <div className="form-grid">
                <div className="form-group">
                  <div className="org-brand-label-row">
                    <label>{t("employeeEdit.form.fullName")} </label>
                    <CustomTooltip
                      tooltip={{
                        message: t("employeeEdit.tooltips.nameLocked"),
                      }}
                      id="organization-edit-name-lock-tooltip"
                      className="org-brand-name-tooltip"
                    >
                      <button
                        type="button"
                        className="employee-edit-tooltip-trigger"
                        aria-label={t("employeeEdit.tooltips.nameLocked")}
                        onClick={(event) => event.currentTarget.focus()}
                      >
                        <Info size={14} />
                      </button>
                    </CustomTooltip>
                  </div>
                  <input
                    disabled
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    placeholder={t("employeeEdit.form.fullName")}
                  />
                </div>

                <div className="form-group">
                  <label>{t("employeeEdit.form.email")}</label>
                  <input
                    disabled
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    readOnly
                    required
                    placeholder={t("employeeEdit.form.email")}
                  />
                </div>

                <div className="form-group">
                  <label>{t("employeeEdit.form.level")}</label>
                  <select
                    value={formData.level}
                    onChange={(e) =>
                      setFormData({ ...formData, level: e.target.value })
                    }
                  >
                    <option value="">{t("common.selectLevel")}</option>
                    {levels.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t("employeeEdit.form.manager")}</label>
                  <select
                    value={formData.manager}
                    onChange={(e) =>
                      setFormData({ ...formData, manager: e.target.value })
                    }
                  >
                    <option value="">
                      {t("employeeEdit.form.selectManager")}
                    </option>
                    {managers.map((manager, i) => (
                      <option key={i} value={manager.id}>
                        {manager.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* <div className="form-group">
                  <label>{t("employeeEdit.form.workStyle")}</label>
                  <select
                    value={formData.workStyle}
                    onChange={(e) =>
                      setFormData({ ...formData, workStyle: e.target.value })
                    }
                  >
                    <option value="">{t("common.selectWorkStyle")}</option>
                    {workStyles?.map((style) => (
                      <option key={style} value={style}>
                        {style}
                      </option>
                    ))}
                  </select>
                </div> */}
                <div className="form-group">
                  <label>{t("employeeEdit.form.department")}</label>
                  <select
                    value={formData.department}
                    onChange={(e) =>
                      setFormData({ ...formData, department: e.target.value })
                    }
                  >
                    <option value="">
                      {t("employeeEdit.form.selectDepartment")}
                    </option>
                    {departmentsList?.map((style) => (
                      <option key={style._id} value={style.name}>
                        {style.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t("employeeEdit.form.performanceRating")}</label>
                  <select
                    value={formData.performanceRating}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        performanceRating: e.target.value,
                      })
                    }
                  >
                    <option value="">
                      {t("common.selectPerformanceRating")}
                    </option>
                    {performanceRatings.map((rating) => (
                      <option key={rating} value={rating}>
                        {rating}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t("employeeEdit.form.availability")}</label>
                  <select
                    value={formData.availability}
                    onChange={(e) =>
                      setFormData({ ...formData, availability: e.target.value })
                    }
                  >
                    <option value="">{t("common.selectAvailability")}</option>
                    {availabilityOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t("employeeEdit.form.role")}</label>
                  <select
                    value={formData.roleJobId || ""}
                    disabled={
                      rolesLoading ||
                      (roleOptions.suggestedRoles.length === 0 &&
                        roleOptions.allJobRoles.length === 0)
                    }
                    onChange={(e) => {
                      const jobId = e.target.value;
                      const roleName =
                        e.target.selectedOptions[0]?.dataset.roleName || "";
                      setFormData((prev) => ({
                        ...prev,
                        roleJobId: jobId,
                        roleName,
                      }));
                    }}
                  >
                    <option value="">
                      {rolesLoading
                        ? t("employeeEdit.form.loadingRoles")
                        : t("employeeEdit.form.selectRole")}
                    </option>

                    {suggestedList.length > 0 && (
                      <optgroup
                        label={t("employeeEdit.form.suggestedRoles", {
                          count: suggestedList.length,
                        })}
                      >
                        {suggestedList.map(
                          ({ jobId, roleName, matchPercentage }) => (
                            <option
                              key={`sugg-${jobId}`}
                              value={jobId}
                              data-role-name={roleName}
                              title={
                                typeof matchPercentage === "number"
                                  ? t("employeeEdit.form.matchPercent", {
                                      percent: matchPercentage,
                                    })
                                  : undefined
                              }
                            >
                              {roleName}{" "}
                              {typeof matchPercentage === "number"
                                ? `— ${t("employeeEdit.form.matchPercent", {
                                    percent: matchPercentage,
                                  })}`
                                : ""}
                            </option>
                          ),
                        )}
                      </optgroup>
                    )}

                    {allList.length > 0 && (
                      <optgroup
                        label={t("employeeEdit.form.allJobRoles", {
                          count: allList.length,
                        })}
                      >
                        {allList.map(({ jobId, roleName }) => (
                          <option
                            key={jobId}
                            value={jobId}
                            data-role-name={roleName}
                          >
                            {roleName}
                          </option>
                        ))}
                      </optgroup>
                    )}

                    {!rolesLoading &&
                      suggestedList.length === 0 &&
                      allList.length === 0 && (
                        <option disabled>
                          {t("employeeEdit.form.noRolesAvailable")}
                        </option>
                      )}
                  </select>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => onNavigate()}
              >
                {t("employeeEdit.actions.cancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmitApiHandler}
              >
                <Save size={16} />
                {t("employeeEdit.actions.saveChanges")}
              </button>
            </div>
          </form>
        </div>
      </main>

      <Modal
        show={showModal.show}
        onClose={handleModalClose}
        showButton={showModal.showButton}
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
      />

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default EmployeeEditPage;
